import type { SchemaDescription } from '@solum/sdk'

/**
 * ONE answer to "what is this record connected to", for every surface.
 *
 * ── The defect ──────────────────────────────────────────────────────────────
 * On `origin/main` FOUR implementations answered that question, and three of
 * them disagreed about what a GROUP is:
 *
 *   apps/console/src/record/RelationCascade.tsx   slot = relType:direction:otherEntity
 *   apps/console/src/app/ShellDrawer.tsx :389     group = relType   (direction lost)
 *   apps/portal/src/PortalDrawer.tsx              group = relType:reading
 *   apps/console/src/app/PartyRelations.tsx       party facts, deliberately separate
 *
 * So the same record answers "how many Accounts?" differently depending on
 * which surface you ask. Worse, the console drawer labelled its groups with
 * `rel_type.replace(/_/g,' ')` — the storage KEY dressed as a heading — while
 * the portal, ten metres away in the same repo, resolved the registered label
 * and its `inverse_label` properly.
 *
 * ── The reading IS the label, and that is why direction is in the key ───────
 * `works_at` outbound is "Works at"; inbound is "Employs". Collapsing the two
 * makes an organisation "work at" its own staff. The portal had this right and
 * the console drawer did not, so this module takes the portal's key.
 *
 * ── This module is PURE ─────────────────────────────────────────────────────
 * No React, no fetching, no `window`. Titles, icons and reachability are
 * resolved by the HOST — the console through `useRecordIdentities`, the portal
 * through `core.titles_of` — because who may see a record is a governed
 * question and this file must not be able to answer it.
 */

/** Which way this record reads the edge. */
export type ConnectionReading = 'out' | 'in' | 'both'

/** One end of one edge, as a host hands it over. */
export interface ConnectionItem {
  /** Stable per-row key; the host owns its shape. */
  key: string
  relType: string
  reading: ConnectionReading
  /** The entity at the OTHER end. */
  entity: string
  id: string
  /**
   * The far record's name, or null when the caller may not see it. NULL IS NOT
   * A FAILURE: `core.titles_of` is `security invoker`, so an unreachable record
   * simply does not come back, and the row still says the relationship exists.
   */
  title?: string | null
  subtitle?: string | null
  iconKey?: string | null
  /** `core.entity_identity.title_declared` — false means "count it, do not name it". */
  nameable?: boolean
  /** Whether the host can actually open it. A row that cannot open is not a button. */
  openable?: boolean
  /** `valid_to` is set: history, shown and marked, never hidden. */
  ended?: boolean
  /** Facts the LINK carries, already named by the host (`attributes_schema`). */
  facts?: readonly string[]
}

/** One relationship, in the reading this record has of it. */
export interface ConnectionGroup {
  /**
   * The HOST's stable key for this group, and the DOM anchor a surface scrolls
   * to. `groupConnections` emits `<relType>:<reading>`, which is the right
   * grain for a panel that summarises a record. A host whose working grain is
   * finer says so in its own key — the record page groups per registered SLOT
   * (`<relType>:<direction>:<otherEntity>`), because the slot is what
   * `core.related_order` states a position for, what `relationship_frequency`
   * counts, and what an Add actually writes into.
   */
  key: string
  relType: string
  reading: ConnectionReading
  label: string
  /**
   * The far entity's governed glyph — `core.entity_identity.icon_key` (#2349 §D2).
   *
   * OPTIONAL, and absent means NO icon rather than a stand-in. The design asks
   * each relationship-type header to show "its icon"; a group whose far entity
   * declares none, or a polymorphic group with no single far entity, has no
   * icon to show, and `CircleDot` beside a name would be this surface inventing
   * an identity the registry did not state. Same rule as `count`: an absent fact
   * is not a neutral one.
   */
  iconKey?: string | null
  items: ConnectionItem[]
  /**
   * Per far-entity totals — the count face, for a surface that shows no rows.
   *
   * `count` is `null` for the same reason the group's is (#2131): a face that
   * renders "People: 0" over a slot nobody counted states an absence nobody
   * measured, and this is the layer `countFace` actually reads.
   */
  byType: { entity: string; label: string; count: number | null }[]
  /**
   * The total this group will stand behind, or **`null` when there is none**.
   *
   * ── why this is nullable (#2131) ──────────────────────────────────────────
   * It was `number`, and `slotConnectionGroups` resolved it as
   * `counted?.n ?? items.length`. A registered slot with no count row and no
   * loaded page therefore arrived as literal `0` — so a relationship the server
   * never counted, or refused to count, rendered as one a person had been told
   * was empty. `ConnectionsProps.isGroupEmpty` has documented that exact
   * collapse since #1992 (*"a count of 0 and a count the server refused to
   * derive arrive as the same number"*) and had to be introduced precisely
   * because this field could not carry the difference. Now it can.
   *
   * Three states, and every consumer must tell them apart deliberately:
   *
   *   · a number > 0  — measured, or a floor when `capped`
   *   · `0`           — MEASURED zero. Only `core.related_counts` produces it;
   *                     it is the only evidence that earns "nothing here yet"
   *   · `null`        — unknown. No count row, and no page we can call a total
   *
   * `null > 0` is silently `false` in TypeScript, so a surviving `count > 0`
   * compiles and reads unknown as empty. That is the defect, not the fix: every
   * consumer below states which of the three it is answering for.
   */
  count: number | null
  /**
   * `core.related_counts` caps at `p_cap` and reports it. A capped group says
   * "at least N", never a number it cannot stand behind.
   */
  capped: boolean
}

/** A server count row, `core.related_counts` shape, per far-entity. */
export interface ConnectionCount {
  relType: string
  reading: ConnectionReading
  entity: string
  n: number
  capped?: boolean
}

/** `current_phase` → `Current phase`, for a key the registry never described. */
export function humanizeKey(key: string): string {
  const s = key.replace(/_id$/, '').replace(/_/g, ' ').trim()
  return s ? s[0]!.toUpperCase() + s.slice(1) : key
}

/**
 * The registered heading for one reading of one relationship.
 *
 * Registry first, `inverse_label` for the inbound reading, humanised key only
 * when the metamodel is silent — a schema that will not load must degrade to a
 * readable heading, not take the panel down with it.
 */
export function connectionLabel(
  relType: string, reading: ConnectionReading, schema?: SchemaDescription,
): string {
  const rt = (schema?.relationships ?? []).find((r) => r.key === relType)
  // A governed symmetric relationship has one reading, not an arbitrarily
  // chosen storage direction. Its two registered labels are equal by the
  // symmetry predicate, so use the canonical label for the `both` surface.
  const declared = reading === 'in' ? rt?.inverse_label : rt?.label
  return declared?.trim() || humanizeKey(relType)
}

const entityLabelOf = (entity: string, schema?: SchemaDescription): string =>
  (schema?.entities ?? []).find((e) => e.key === entity)?.label?.trim() || humanizeKey(entity)

/** The Timeline owns these; showing them here makes one interaction look like two facts. */
export const isTimelineRelationship = (relType: string): boolean => relType.startsWith('activity_')

/**
 * Group the edges a host has resolved.
 *
 * `counts` is OPTIONAL and authoritative where it has an answer:
 * `core.related_counts` is a server-side count over rows the caller may see,
 * and it knows about rows this page has not loaded. A missing count row never
 * falls back to the loaded page. Those rows are only lower-bound evidence — a
 * host may have loaded one page of a much larger relationship — so the group
 * remains unknown and `connectionTotal` uses the visible rows only as a floor.
 */
export function groupConnections(
  items: readonly ConnectionItem[],
  schema?: SchemaDescription,
  counts?: readonly ConnectionCount[],
): ConnectionGroup[] {
  const groups = new Map<string, ConnectionGroup>()
  const seen = new Set<string>()

  const groupFor = (relType: string, reading: ConnectionReading): ConnectionGroup => {
    const key = `${relType}:${reading}`
    let g = groups.get(key)
    if (!g) {
      g = { key, relType, reading, label: connectionLabel(relType, reading, schema),
            items: [], byType: [], count: null, capped: false }
      groups.set(key, g)
    }
    return g
  }

  for (const item of items) {
    if (isTimelineRelationship(item.relType)) continue
    // The same far record can arrive twice when a host reads both ends of a
    // symmetric relationship. One edge, one row.
    const once = `${item.relType}:${item.reading}:${item.entity}:${item.id}`
    if (seen.has(once)) continue
    seen.add(once)
    const g = groupFor(item.relType, item.reading)
    g.items.push(item)
    const t = g.byType.find((x) => x.entity === item.entity)
    // A loaded row proves only that AT LEAST one row exists. It is not count
    // evidence: this may be one truncated page, or the count door may have
    // refused. Keep the face unknown until a count row below answers it.
    if (!t) g.byType.push({ entity: item.entity, label: entityLabelOf(item.entity, schema), count: null })
  }

  for (const c of counts ?? []) {
    if (isTimelineRelationship(c.relType)) continue
    const g = groupFor(c.relType, c.reading)
    const t = g.byType.find((x) => x.entity === c.entity)
    if (t) t.count = c.n
    else g.byType.push({ entity: c.entity, label: entityLabelOf(c.entity, schema), count: c.n })
    if (c.capped) g.capped = true
  }

  for (const g of groups.values()) {
    g.byType.sort((a, b) => a.label.localeCompare(b.label))
    // Exact only when EVERY far-entity face has count evidence. A partially
    // answered group remains unknown: summing the answered faces and the
    // loaded rows would still turn a page into a total. `connectionTotal`
    // retains both pieces as lower-bound evidence without changing this claim.
    g.count = g.byType.every((t) => t.count !== null)
      ? g.byType.reduce((n, t) => n + (t.count ?? 0), 0)
      : null
  }

  return [...groups.values()]
    // `(g.count ?? 0) > 0`, not `g.count > 0`: the latter compiles against the
    // widened type and reads UNKNOWN as "drop it", which would hide exactly the
    // groups #2131 exists to keep visible. Unknown is not a reason to delete a
    // relationship from a surface — it is a reason not to put a number on it.
    .filter((g) => (g.count ?? 0) > 0 || g.items.length > 0)
    .sort((a, b) => a.label.localeCompare(b.label) || a.key.localeCompare(b.key))
}

/** How a surface was told to arrange the groups. Resolved by the host from the
 *  `core.related_order` cascade (persona preset → deployment → user) — this
 *  module never reads or writes a preference of its own. */
export interface ConnectionArrangement {
  /** Group keys to show first, in this order. */
  pinned?: readonly string[]
  /** Group keys the arrangement hides. Hidden is not deleted: the count stays reachable. */
  hidden?: readonly string[]
  /**
   * Group keys placed by a FORM DEFINITION rather than by the count heuristic
   * (#1466). Purely descriptive: it changes no order — `pinned` already carries
   * the placement — and exists so a reader can tell an authored position from a
   * derived one.
   *
   * The distinction is not cosmetic. `76fee8cc` ruled that a definition's
   * related sections lead the cascade, and ROOT ruled that count-descending
   * applies only to the groups a definition did NOT place. Without a marker the
   * two are indistinguishable in the DOM, and an assertion about "default"
   * ordering can only be written over all of them — which is exactly how
   * #2018's became over-broad.
   */
  declared?: readonly string[]
}

/**
 * Pin, hide, and OTHERWISE LEAVE THE CALLER'S ORDER ALONE.
 *
 * The unpinned tail used to be re-sorted by label here. That was invisible while
 * the only caller fed it `groupConnections`' output — which is already
 * label-sorted, so the second sort was a no-op — and it became a defect the
 * moment the record page arrived (#1466): the cascade orders its groups
 * *populated → this person's frequency → alphabetical*, and a re-sort here
 * silently threw that away and put the twenty empty relationships back among the
 * two that have rows.
 *
 * So the rule is: this function applies the STATEMENT (pinned, hidden) and
 * nothing else. Whatever order the host handed over survives it —
 * `Array.prototype.sort` is stable, so equal ranks keep their input order — and
 * a host with no order of its own passes `groupConnections`' output, which is
 * alphabetical because THAT function sorted it.
 */
export function arrangeConnections(
  groups: readonly ConnectionGroup[], arrangement?: ConnectionArrangement,
): ConnectionGroup[] {
  const pinned = arrangement?.pinned ?? []
  const hidden = new Set(arrangement?.hidden ?? [])
  const rank = new Map(pinned.map((key, i) => [key, i]))
  return groups
    .filter((g) => !hidden.has(g.key))
    .slice()
    .sort((a, b) =>
      (rank.get(a.key) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.key) ?? Number.MAX_SAFE_INTEGER))
}

/**
 * Total across groups, whether any of them could only give a floor, and
 * whether any of them could not be counted at all.
 *
 * ── `unknown` is not decoration (#2131) ────────────────────────────────────
 * Summing a nullable count means deciding what an unknown group contributes.
 * Treating it as 0 under-states the total and calls a slot empty; treating the
 * loaded page as an exact count over-states what was measured. The defensible
 * answer is a floor: server counts for the faces it answered, plus visible rows
 * for faces it did not, with `unknown` set so the surface says "at least N".
 *
 * That makes `n` a FLOOR whenever `unknown` or `atLeast` is set. The two are
 * kept apart because they are different facts: `atLeast` is the counts door
 * hitting `p_cap` and saying so, `unknown` is a slot or face it never answered.
 */
export function connectionTotal(
  groups: readonly ConnectionGroup[],
): { n: number; atLeast: boolean; unknown: boolean } {
  const floorOf = (group: ConnectionGroup): number => {
    if (group.count !== null) return group.count

    // Counted faces remain evidence even when a sibling face is unknown. For
    // each unknown face, visible rows are the only defensible floor. Do not add
    // visible rows for a counted face as well: its server count already
    // includes them.
    const byEntity = new Map(group.byType.map((face) => [face.entity, face.count] as const))
    const counted = group.byType.reduce((n, face) => n + (face.count ?? 0), 0)
    const visibleUnknown = group.items.filter((item) => (byEntity.get(item.entity) ?? null) === null).length
    return counted + visibleUnknown
  }

  return {
    n: groups.reduce((n, g) => n + floorOf(g), 0),
    atLeast: groups.some((g) => g.capped),
    unknown: groups.some((g) => g.count === null),
  }
}
